import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FileDown, Contact, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { resumesAdminAPI, AdminResumeListItem } from '../services/api';
import { DEFAULT_TEMPLATES, ResumeData, Template, UserOverrides } from '../resume-preview/resumeTypes';
import { normalizeResumeForPdf } from '../resume-preview/pdfGenerator';
import ResumeRenderer from '../resume-preview/ResumeRenderer';
import PdfExportFromPreviewLayer from '../resume-preview/PdfExportFromPreviewLayer';
import { apiResumeToResumeData } from './resumeAdminMerge';

function blocksTooltip(blocks: { filled: boolean; label: string }[]): string {
  return blocks.map((b) => `${b.filled ? '✓' : '✗'} ${b.label}`).join('\n');
}

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const ResumesPage: React.FC = () => {
  const [list, setList] = useState<AdminResumeListItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [template, setTemplate] = useState<Template>(DEFAULT_TEMPLATES[0]);
  const [overrides, setOverrides] = useState<UserOverrides>({});
  const [pdfExport, setPdfExport] = useState<{
    resume: ResumeData;
    template: Template;
    overrides: UserOverrides;
  } | null>(null);

  const previewHostRef = useRef<HTMLDivElement>(null);
  const [previewFit, setPreviewFit] = useState<{ scale: number; w: number; h: number } | null>(null);

  const loadList = useCallback(async (page = 1, append = false) => {
    try {
      setLoadingList(true);
      const data = await resumesAdminAPI.list(page, 100);
      if (data.success) {
        if (append) {
          setList((prev) => [...prev, ...(data.resumes || [])]);
        } else {
          setList(data.resumes || []);
        }
        setPagination(data.pagination);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить список резюме');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList(1, false);
  }, [loadList]);

  useLayoutEffect(() => {
    if (!resumeData) {
      setPreviewFit(null);
      return;
    }
    const host = previewHostRef.current;
    if (!host) return;

    const measure = () => {
      const sheet = host.querySelector('.a4-sheet') as HTMLElement | null;
      if (!sheet) return;
      const cw = host.clientWidth;
      const rect = sheet.getBoundingClientRect();
      if (cw <= 0 || rect.width <= 0) return;
      const scale = cw / rect.width;
      setPreviewFit({ scale, w: rect.width, h: rect.height });
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    ro.observe(host);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        measure();
        const sheetEl = host.querySelector('.a4-sheet');
        if (sheetEl) ro.observe(sheetEl);
      });
    });
    void document.fonts.ready.then(measure);

    return () => ro.disconnect();
  }, [resumeData, template.id, overrides, selectedId]);

  const applyDetailToPreview = useCallback((raw: Record<string, unknown>) => {
    const merged = apiResumeToResumeData(raw as Record<string, any>);
    const templateIdRaw =
      typeof raw.template === 'string'
        ? raw.template
        : (raw.template as { id?: string } | undefined)?.id;
    const aliases: Record<string, string> = {
      'modern-sidebar-dark': 'template-03',
      'modern-sidebar-light': 'template-03',
      'monochrome-minimal': 'template-02',
      classic: 'template-01',
      minimal: 'template-02',
      creative: 'template-01',
      professional: 'template-01',
      elegant: 'template-01',
    };
    const resolvedId = aliases[templateIdRaw || ''] || templateIdRaw || 'template-01';
    const tpl = DEFAULT_TEMPLATES.find((t) => t.id === resolvedId) || DEFAULT_TEMPLATES[0];
    const scheme = (raw as Record<string, unknown>).colorScheme ?? (raw as Record<string, unknown>).color_scheme;
    const savedColorsFromScheme =
      scheme && typeof scheme === 'object' ? (scheme as UserOverrides['colors']) : undefined;
    const savedColorsFromTemplate =
      raw.template && typeof raw.template === 'object' && raw.template !== null
        ? (raw.template as { colors?: UserOverrides['colors'] })?.colors
        : undefined;
    const savedColors = savedColorsFromScheme ?? savedColorsFromTemplate;
    setTemplate(tpl);
    setOverrides(savedColors ? { colors: savedColors } : {});
    setResumeData(merged);
  }, []);

  const selectResume = useCallback(
    async (id: number) => {
      setSelectedId(id);
      setDetailLoading(true);
      setResumeData(null);
      try {
        const data = await resumesAdminAPI.get(id);
        if (!data.success || !data.resume) {
          toast.error('Резюме не найдено');
          return;
        }
        applyDetailToPreview(data.resume as Record<string, unknown>);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Ошибка загрузки резюме');
        setSelectedId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [applyDetailToPreview]
  );

  const handleDownloadPdf = () => {
    if (!resumeData) return;
    const normalized = normalizeResumeForPdf(resumeData);
    // Как на превью и в PdfExportFromPreviewLayer у откликов: тот же template/overrides, что у ResumeRenderer.
    setPdfExport({ resume: normalized, template, overrides });
  };

  const selectedRow = list.find((r) => r.id === selectedId) || null;

  const handleDownloadOriginal = async () => {
    if (!selectedId) return;
    try {
      await resumesAdminAPI.downloadOriginal(selectedId, selectedRow?.originalFileName ?? undefined);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось получить оригинал');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Contact className="h-8 w-8 text-blue-600" />
          Резюме
        </h1>
        <p className="text-gray-600 mt-2">Все резюме пользователей (консультант и «Мои резюме»)</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-12rem)] lg:items-stretch">
        <div className="w-full lg:w-1/2 lg:min-w-0 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-sm text-gray-500">
            Всего: {pagination.total}
            {pagination.pages > 1 && (
              <span className="ml-2">
                (стр. {pagination.page} / {pagination.pages})
              </span>
            )}
          </div>
          <div className="overflow-auto flex-1 max-h-[calc(100vh-14rem)]">
            {loadingList ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : list.length === 0 ? (
              <p className="p-4 text-gray-500">Нет резюме</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2">Название</th>
                    <th className="px-2 py-2">Пользователь</th>
                    <th className="px-2 py-2">Источник</th>
                    <th className="px-2 py-2">Активность</th>
                    <th className="px-2 py-2 w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => selectResume(row.id)}
                      className={`cursor-pointer border-t border-gray-100 hover:bg-gray-50 ${
                        selectedId === row.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-2 py-2 font-medium text-gray-900 max-w-[8rem] truncate" title={row.title}>
                        {row.title}
                      </td>
                      <td className="px-2 py-2 max-w-[10rem]">
                        <div className="truncate font-medium text-gray-800" title={row.userFullName}>
                          {row.userFullName}
                        </div>
                        <div className="truncate text-xs text-gray-500" title={row.userEmail}>
                          {row.userEmail}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                            row.source === 'consultant'
                              ? 'bg-purple-100 text-purple-800'
                              : row.source === 'file_upload'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                          title={row.originalFileName ?? undefined}
                        >
                          {row.sourceLabel}
                          {row.originalFileName && (
                            <span className="ml-1 opacity-70">· {row.originalFileName}</span>
                          )}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {formatDt(row.lastActivityAt)}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className="font-semibold text-blue-700 cursor-help"
                          title={blocksTooltip(row.blocks)}
                        >
                          {row.fillPercent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {pagination.pages > 1 && pagination.page < pagination.pages && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => loadList(pagination.page + 1, true)}
                className="text-sm text-blue-600 hover:underline"
              >
                Загрузить ещё (стр. {pagination.page + 1})
              </button>
            </div>
          )}
        </div>

        <div className="w-full lg:w-1/2 lg:min-w-0 flex flex-col border border-gray-200 rounded-lg bg-white min-h-[24rem]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm text-gray-600">
              {selectedId ? `Резюме #${selectedId}` : 'Превью'}
            </span>
            <div className="flex items-center gap-2">
              {selectedRow?.hasOriginalFile && (
                <button
                  type="button"
                  onClick={handleDownloadOriginal}
                  title={selectedRow.originalFileName ?? 'Оригинальный файл'}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  <FileDown className="h-4 w-4" />
                  Скачать оригинал
                </button>
              )}
              <button
                type="button"
                disabled={!resumeData || detailLoading}
                onClick={handleDownloadPdf}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDown className="h-4 w-4" />
                Скачать PDF
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto bg-slate-100 p-3 sm:p-4 flex flex-col">
            {detailLoading && (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
            )}
            {!detailLoading && !resumeData && (
              <div className="flex flex-1 items-center justify-center text-gray-500 text-center px-6">
                Выберите резюме из списка слева
              </div>
            )}
            {!detailLoading && resumeData && (
              <div
                ref={previewHostRef}
                className="w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
              >
                <div
                  className="mx-auto"
                  style={
                    previewFit
                      ? {
                          width: previewFit.w * previewFit.scale,
                          height: previewFit.h * previewFit.scale,
                          overflow: 'hidden',
                        }
                      : { minHeight: 400 }
                  }
                >
                  <div
                    style={
                      previewFit
                        ? {
                            transform: `scale(${previewFit.scale})`,
                            transformOrigin: 'top left',
                            width: previewFit.w,
                          }
                        : undefined
                    }
                  >
                    <div className="a4-preview-container shadow-lg">
                      <ResumeRenderer data={resumeData} template={template} overrides={overrides} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {pdfExport && (
        <PdfExportFromPreviewLayer
          resume={pdfExport.resume}
          template={pdfExport.template}
          overrides={pdfExport.overrides}
          mode="download"
          onDone={() => setPdfExport(null)}
          onError={(msg) => toast.error(msg)}
        />
      )}
    </div>
  );
};

export default ResumesPage;
